import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HashRouter,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import Camera from './components/Camera';
import SignaturePad from './components/SignaturePad';
import ThumbStrip from './components/ThumbStrip';
import {
  clearDraft,
  db,
  deleteJob,
  loadDraft,
  saveApprovedJob,
  saveDraft
} from './db/schema';
import {
  clearLogo,
  DEFAULT_SETTINGS,
  getSettings,
  listClients,
  saveClient,
  saveLogo,
  saveSettings
} from './db/schema';
import { LOGO_MAX_DIM, normalizeLogo, svgDataUrl } from './lib/settings';
import {
  NOMINATIM_ATTRIBUTION,
  NOMINATIM_POLICY_NOTE,
  searchAddress
} from './lib/address';
import { captureGeotag, formatGeotag } from './lib/geotag';
import {
  archiveRecords,
  deleteRecordsPermanently,
  getOlderRecordIds,
  getRecordCounts,
  restoreRecords
} from './lib/archive';
import { exportBackup, importBackup } from './db/backup';
import { createPdf } from './lib/pdf';
import {
  downloadBlob,
  openBlob,
  safeFileName,
  sharePdf
} from './lib/share';
import {
  formatBytes,
  requestPersistentStorage,
  storageEstimate
} from './lib/storage';
import './App.css';

const PDF_SAVE_TIMEOUT_MS = 15000;

const emptyDraft = () => ({
  images: [],
  title: '',
  client: '',
  jobRef: '',
  address: '',
  notes: '',
  tags: '',
  geotag: null
});

function withSaveTimeout(promise) {
  let timer;

  const timeout = new Promise((_, reject) => {
    timer = window.setTimeout(() => {
      reject(new Error('Save timed out during saving after 15 seconds.'));
    }, PDF_SAVE_TIMEOUT_MS);
  });

  return Promise.race([promise, timeout]).finally(() => {
    window.clearTimeout(timer);
  });
}

async function hashPdf(pdf) {
  const bytes = await pdf.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', bytes);

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function AppShell() {
  const [draft, setDraft] = useState(emptyDraft);
  const [notice, setNotice] = useState('');
  const [resume, setResume] = useState(null);
  const [installEvent, setInstallEvent] = useState(null);

  useEffect(() => {
    requestPersistentStorage().catch(() => {});

    loadDraft()
      .then((saved) => {
        if (saved?.images?.length) setResume(saved);
      })
      .catch(() => setNotice('The saved draft could not be read.'));

    const install = (event) => {
      event.preventDefault();
      setInstallEvent(event);
    };

    window.addEventListener('beforeinstallprompt', install);

    return () => window.removeEventListener('beforeinstallprompt', install);
  }, []);

  const updateDraft = useCallback((next) => {
    setDraft((current) => {
      const value = typeof next === 'function' ? next(current) : next;

      saveDraft(value).catch((error) => setNotice(error.message));
      return value;
    });
  }, []);

  return (
    <div className="app">
      {notice && (
        <button
          type="button"
          className="toast"
          onClick={() => setNotice('')}
        >
          {notice}
        </button>
      )}

      {resume && (
        <div className="resume-banner">
          <span>
            Unsaved job found with {resume.images.length} photo
            {resume.images.length === 1 ? '' : 's'}.
          </span>
          <button
            type="button"
            onClick={() => {
              setDraft(resume);
              setResume(null);
              location.hash = '#/capture';
            }}
          >
            Resume draft
          </button>
          <button
            type="button"
            onClick={() => {
              clearDraft().catch(() => {});
              setResume(null);
            }}
          >
            Discard
          </button>
        </div>
      )}

      <Routes>
        <Route
          path="/"
          element={
            <Capture
              draft={draft}
              setDraft={updateDraft}
              setNotice={setNotice}
            />
          }
        />
        <Route
          path="/capture"
          element={
            <Capture
              draft={draft}
              setDraft={updateDraft}
              setNotice={setNotice}
            />
          }
        />
        <Route
          path="/review"
          element={
            <Review
              draft={draft}
              setDraft={updateDraft}
              setNotice={setNotice}
            />
          }
        />
        <Route
          path="/success/:id"
          element={
            <Success
              setDraft={setDraft}
              setNotice={setNotice}
            />
          }
        />
        <Route path="/records" element={<Records />} />
        <Route
          path="/records/:id"
          element={<RecordDetail setNotice={setNotice} />}
        />
        <Route
          path="/settings"
          element={
            <Settings
              installEvent={installEvent}
              setNotice={setNotice}
            />
          }
        />
      </Routes>

      <BottomNav />
    </div>
  );
}

function Capture({ draft, setDraft, setNotice }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const add = useCallback(
    (image) => {
      setDraft((current) => ({
        ...current,
        images: [
          ...current.images,
          {
            ...image,
            key: crypto.randomUUID(),
            capturedAt: image.capturedAt || Date.now(),
            gps: current.geotag || null
          }
        ]
      }));
    },
    [setDraft]
  );

  const files = async (event) => {
    try {
      const { processImage } = await import('./lib/image');

      for (const file of Array.from(event.target.files || [])) {
        add(await processImage(file));
      }
    } catch (error) {
      setNotice(error.message);
    } finally {
      event.target.value = '';
    }
  };

  return (
    <main className="screen capture-screen">
      <header className="topbar">
        <div>
          <small>New proof</small>
          <h1>Capture work</h1>
        </div>
        <strong>{draft.images.length}</strong>
      </header>

      <Camera onAdd={add} onError={setNotice} />

      <ThumbStrip
        images={draft.images}
        onDelete={(index) =>
          setDraft((value) => ({
            ...value,
            images: value.images.filter((_, itemIndex) => itemIndex !== index)
          }))
        }
        onMove={(index, direction) =>
          setDraft((value) => {
            const target = index + direction;

            if (target < 0 || target >= value.images.length) return value;

            const images = [...value.images];
            [images[index], images[target]] = [
              images[target],
              images[index]
            ];

            return { ...value, images };
          })
        }
        onPreview={(index) => openBlob(draft.images[index].image)}
      />

      <div className="action-dock">
        <button
          type="button"
          className="button secondary"
          onClick={() => inputRef.current.click()}
        >
          Add photos
        </button>
        <button
          type="button"
          className="button primary"
          disabled={!draft.images.length}
          onClick={() => navigate('/review')}
        >
          Done · {draft.images.length}
        </button>
        <input
          ref={inputRef}
          hidden
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          onChange={files}
        />
      </div>
    </main>
  );
}

function CaptionThumb({ image }) {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!image?.thumbnail) {
      setUrl('');
      return undefined;
    }

    const next = URL.createObjectURL(image.thumbnail);
    setUrl(next);

    return () => URL.revokeObjectURL(next);
  }, [image?.thumbnail]);

  return url ? (
    <img className="caption-thumb" src={url} alt="" />
  ) : null;
}

const FALLBACK_SIGNER_TYPES = [
  'Client',
  'Contractor',
  'Site Manager',
  'Director'
];

function Review({ draft, setDraft, setNotice }) {
  const signatureRef = useRef(null);
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [stage, setStage] = useState('idle');
  const [progress, setProgress] = useState(0);

  const [signerName, setSignerName] = useState('');
  const [signerType, setSignerType] = useState('');
  const [signerTypes, setSignerTypes] = useState([]);

  const [addressQuery, setAddressQuery] = useState(draft.address || '');
  const [addressResults, setAddressResults] = useState([]);
  const [addressSearching, setAddressSearching] = useState(false);
  const [addressCached, setAddressCached] = useState(false);
  const searchTimer = useRef(null);
  const searchAbort = useRef(null);

  const [captionFor, setCaptionFor] = useState(null);
  const [captionText, setCaptionText] = useState('');
  const captionInputRef = useRef(null);

  useEffect(() => {
    listClients()
      .then((rows) => setClients(rows.map((row) => row.name)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getSettings()
      .then((value) => setSignerTypes(value.signerTypes || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      window.clearTimeout(searchTimer.current);
      searchAbort.current?.abort();
    };
  }, []);

  const runAddressSearch = useCallback(async (value) => {
    if (!value || value.trim().length < 3) {
      setAddressResults([]);
      return;
    }

    searchAbort.current?.abort();
    const controller = new AbortController();
    searchAbort.current = controller;
    setAddressSearching(true);

    try {
      const { results, cached } = await searchAddress(value, {
        signal: controller.signal
      });

      setAddressResults(results);
      setAddressCached(cached);
    } catch (error) {
      if (error?.name === 'AbortError') return;

      setAddressResults([]);
      setNotice(error.message);
    } finally {
      if (!controller.signal.aborted) setAddressSearching(false);
    }
  }, [setNotice]);

  const onAddressInput = (event) => {
    const value = event.target.value;

    setAddressQuery(value);
    setDraft((current) => ({ ...current, address: value }));
    window.clearTimeout(searchTimer.current);

    // Offline-safe: debounced search, and manual entry remains fully usable.
    searchTimer.current = window.setTimeout(() => {
      runAddressSearch(value);
    }, 700);
  };

  const pickAddress = (result) => {
    const oneLine = result.label;

    setAddressQuery(oneLine);
    setAddressResults([]);
    setDraft((current) => ({ ...current, address: oneLine }));
  };

  const grabGeotag = async () => {
    try {
      const gps = await captureGeotag();

      setDraft((current) => ({
        ...current,
        geotag: gps,
        images: current.images.map((image) => ({ ...image, gps }))
      }));
      setNotice(
        `Location captured: ${formatGeotag(gps)}. New photos will also be tagged.`
      );
    } catch (error) {
      setNotice(error.message);
    }
  };

  const clearGeotag = () => {
    setDraft((current) => ({
      ...current,
      geotag: null,
      images: current.images.map(({ gps, ...image }) => image)
    }));
  };

  const openCaption = (index) => {
    setCaptionFor(index);
    setCaptionText(draft.images[index]?.caption || '');
    window.setTimeout(() => captionInputRef.current?.focus(), 50);
  };

  const saveCaption = () => {
    if (captionFor === null) return;

    const index = captionFor;
    const text = captionText.trim();

    setDraft((current) => ({
      ...current,
      images: current.images.map((image, imageIndex) =>
        imageIndex === index ? { ...image, caption: text || null } : image
      )
    }));
    setCaptionFor(null);
    setCaptionText('');
  };

  const dictateCaption = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setNotice('Voice dictation is not available in this browser. Type the caption instead.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-GB';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const spoken = Array.from(event.results)
        .map((result) => result[0]?.transcript || '')
        .join(' ')
        .trim();

      if (spoken) {
        setCaptionText((current) =>
          current ? `${current} ${spoken}` : spoken
        );
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setNotice('Microphone permission was denied. Type the caption instead.');
      }
    };

    try {
      recognition.start();
    } catch {
      /* already started: ignore */
    }
  };

  const approve = async () => {
    if (!draft.title.trim() || !draft.client.trim()) {
      setNotice('Enter the job title and client name before approving.');
      return;
    }

    if (signatureRef.current.empty()) {
      setNotice('Ask the client to sign before approving.');
      return;
    }

    setStage('processing_images');
    setProgress(5);

    try {
      const signature = await signatureRef.current.toBlob();
      const metadata = {
        title: draft.title.trim(),
        client: draft.client.trim(),
        signerName: signerName.trim(),
        signerType: signerType.trim(),
        jobRef: draft.jobRef.trim(),
        address: draft.address.trim(),
        notes: draft.notes.trim(),
        tags: draft.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        createdAt: Date.now(),
        status: 'approved',
        photoCount: draft.images.length,
        thumbnail: draft.images[0]?.thumbnail || null
      };

      setStage('building_pdf');
      setProgress(10);

      const pdf = await createPdf(
        {
          metadata,
          images: draft.images.map(({ image, width, height, caption, capturedAt, gps }) => ({
            image,
            width,
            height,
            caption,
            capturedAt,
            gps
          })),
          signature
        },
        (value) => {
          setStage('building_pdf');
          setProgress(Math.min(90, Math.max(10, value)));
        }
      );

      const sha256 = await hashPdf(pdf);
      const savedMetadata = {
        ...metadata,
        sha256
      };

      setStage('saving');
      setProgress(95);

      const id = await withSaveTimeout(
        saveApprovedJob(savedMetadata, {
          images: draft.images,
          signature,
          pdf
        })
      );

      await saveClient(metadata.client).catch(() => {});

      setStage('done');
      setProgress(100);
      navigate(`/success/${id}`, { replace: true });
    } catch (error) {
      setStage('error');
      setNotice(
        `${error.message || 'The proof could not be completed.'} The draft, photos, signature, and form data are still saved. Tap Retry to try again.`
      );
    }
  };

  const retry = () => {
    setNotice('');
    approve();
  };

  return (
    <main className="screen review">
      <header className="topbar">
        <div>
          <small>Step 2 of 2</small>
          <h1>Client approval</h1>
        </div>
      </header>

      <section className="form-card">
        <label>
          Job title
          <input
            value={draft.title}
            autoComplete="off"
            onChange={(event) =>
              setDraft({ ...draft, title: event.target.value })
            }
          />
        </label>

        <label>
          Job reference <small>Optional</small>
          <input
            value={draft.jobRef}
            autoComplete="off"
            placeholder="e.g. INV-2041"
            onChange={(event) =>
              setDraft({ ...draft, jobRef: event.target.value })
            }
          />
        </label>

        <label>
          Client name
          <input
            list="clients"
            value={draft.client}
            onChange={(event) =>
              setDraft({ ...draft, client: event.target.value })
            }
          />
        </label>

        <datalist id="clients">
          {clients.map((client) => (
            <option key={client} value={client} />
          ))}
        </datalist>

        <label>
          Signed by <small>Optional, defaults to the client</small>
          <input
            value={signerName}
            autoComplete="off"
            placeholder="Who signed this proof"
            onChange={(event) => setSignerName(event.target.value)}
          />
        </label>

        <label>
          Signing as <small>Optional</small>
          <select
            value={signerType}
            onChange={(event) => setSignerType(event.target.value)}
            style={{
              display: 'block',
              width: '100%',
              minHeight: 48,
              marginTop: 8,
              padding: '0 10px',
              border: '2px solid var(--line)',
              borderRadius: 10,
              background: 'var(--panel)',
              color: 'var(--ink)'
            }}
          >
            <option value="">Not specified</option>
            {(signerTypes.length ? signerTypes : FALLBACK_SIGNER_TYPES).map(
              (type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              )
            )}
          </select>
        </label>

        <label>
          Site address <small>Search or type it</small>
          <input
            value={addressQuery}
            autoComplete="off"
            placeholder="Street, town or postcode"
            onChange={onAddressInput}
          />
        </label>

        {addressSearching && <p className="hint">Searching addresses…</p>}
        {!addressSearching && addressCached && addressResults.length > 0 && (
          <p className="hint">From this device's cache</p>
        )}

        {addressResults.length > 0 && (
          <ul className="address-results" role="listbox" aria-label="Address results">
            {addressResults.map((result) => (
              <li key={`${result.lat}-${result.lon}`}>
                <button type="button" onClick={() => pickAddress(result)}>
                  {result.label}
                </button>
              </li>
            ))}
            <li className="attribution">{NOMINATIM_ATTRIBUTION}</li>
          </ul>
        )}

        <label>
          Notes <small>Optional</small>
          <textarea
            rows="3"
            value={draft.notes}
            onChange={(event) =>
              setDraft({ ...draft, notes: event.target.value })
            }
          />
        </label>

        <label>
          Tags <small>Optional, comma separated</small>
          <input
            value={draft.tags}
            onChange={(event) =>
              setDraft({ ...draft, tags: event.target.value })
            }
          />
        </label>

        <div className="geotag-row">
          {draft.geotag ? (
            <>
              <span className="hint">{formatGeotag(draft.geotag)}</span>
              <button
                type="button"
                className="button ghost"
                onClick={clearGeotag}
              >
                Remove location
              </button>
            </>
          ) : (
            <button
              type="button"
              className="button secondary"
              onClick={grabGeotag}
            >
              Add location stamp
            </button>
          )}
        </div>
        <p className="hint" style={{ marginTop: 6 }}>
          {NOMINATIM_POLICY_NOTE}
        </p>
      </section>

      <section className="form-card">
        <h2>Photo captions</h2>
        {draft.images.map((image, index) => (
          <div className="caption-row" key={image.key}>
            <span className="caption-index">{index + 1}</span>
            <CaptionThumb image={image} />
            <div className="caption-body">
              {captionFor === index ? (
                <>
                  <input
                    ref={captionInputRef}
                    value={captionText}
                    placeholder="Caption for this photo"
                    onChange={(event) => setCaptionText(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        saveCaption();
                      }
                    }}
                  />
                  <div className="caption-chips">
                    {[
                      'Before',
                      'After',
                      'Close-up',
                      'Wide view',
                      'Defect',
                      'Detail'
                    ].map((chip) => (
                      <button
                        type="button"
                        key={chip}
                        className="chip"
                        onClick={() =>
                          setCaptionText((current) =>
                            current ? `${current} ${chip}` : chip
                          )
                        }
                      >
                        {chip}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="chip chip-mic"
                      onClick={dictateCaption}
                      aria-label="Dictate caption"
                      title="Dictate caption"
                    >
                      Mic
                    </button>
                  </div>
                  <div className="caption-actions">
                    <button
                      type="button"
                      className="button primary"
                      onClick={saveCaption}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="button ghost"
                      onClick={() => setCaptionFor(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span className="hint">
                    {image.caption || 'No caption'}
                    {image.capturedAt
                      ? ` · ${new Date(image.capturedAt).toLocaleString()}`
                      : ''}
                  </span>
                  <button
                    type="button"
                    className="button ghost"
                    onClick={() => openCaption(index)}
                  >
                    {image.caption ? 'Edit' : 'Add caption'}
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </section>

      <section className="form-card">
        <h2>Sign to approve</h2>
        <p>I confirm that the work shown was completed and approved.</p>
        <SignaturePad ref={signatureRef} />
      </section>

      {stage !== 'idle' && stage !== 'done' && (
        <section className="form-card">
          <p>
            Stage: <strong>{stage}</strong>
          </p>
          <progress max="100" value={progress} />
          <p>{progress}%</p>
        </section>
      )}

      <div className="action-dock">
        <button
          type="button"
          className="button secondary"
          disabled={stage !== 'idle' && stage !== 'error'}
          onClick={() => navigate('/capture')}
        >
          Back
        </button>

        {stage === 'error' ? (
          <button
            type="button"
            className="button primary"
            onClick={retry}
          >
            Retry
          </button>
        ) : (
          <button
            type="button"
            className="button primary"
            disabled={stage !== 'idle'}
            onClick={approve}
          >
            {stage === 'idle' ? 'Approve' : `${stage} · ${progress}%`}
          </button>
        )}
      </div>
    </main>
  );
}

function useRecord(id) {
  const [record, setRecord] = useState(null);

  useEffect(() => {
    Promise.all([
      db.jobs.get(Number(id)),
      db.blobs.get(Number(id))
    ])
      .then(([job, blobs]) =>
        setRecord(job && blobs ? { ...job, ...blobs } : false)
      )
      .catch(() => setRecord(false));
  }, [id]);

  return record;
}

function Success({ setDraft, setNotice }) {
  const { id } = useParams();
  const record = useRecord(id);
  const navigate = useNavigate();

  if (record === null) {
    return <main className="screen centered">Loading approved proof…</main>;
  }

  if (!record) {
    return (
      <main className="screen centered">
        This proof could not be found.
      </main>
    );
  }

  const name = safeFileName(record.title, id);

  return (
    <main className="screen success centered">
      <div className="approved-mark">✓</div>
      <small>Saved on this device</small>
      <h1>Approved</h1>
      <p>
        {record.title}
        <br />
        <strong>{record.client}</strong>
      </p>
      {record.signerName && <p className="hint">Signed by {record.signerName}</p>}
      {record.signerType && <p className="hint">Signing as {record.signerType}</p>}
      {(record.jobRef || record.address) && (
        <p className="hint">
          {record.jobRef ? `Ref ${record.jobRef}` : ''}
          {record.jobRef && record.address ? ' · ' : ''}
          {record.address || ''}
        </p>
      )}
      {record.sha256 && (
        <p className="fingerprint" title={record.sha256}>
          SHA-256 {record.sha256.slice(0, 16)}…
        </p>
      )}

      <div className="stack">
        <button
          className="button primary"
          onClick={() =>
            sharePdf(record.pdf, name, record.title).catch((error) =>
              setNotice(error.message)
            )
          }
        >
          Share PDF
        </button>
        <button
          className="button secondary"
          onClick={() => openBlob(record.pdf)}
        >
          Open
        </button>
        <button
          className="button secondary"
          onClick={() => downloadBlob(record.pdf, name)}
        >
          Download
        </button>
        <button
          className="button ghost"
          onClick={() => {
            setDraft(emptyDraft());
            navigate('/capture');
          }}
        >
          New job
        </button>
      </div>
    </main>
  );
}

function Records() {
  const [jobs, setJobs] = useState([]);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('active');
  const [selected, setSelected] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [counts, setCounts] = useState({ active: 0, archived: 0 });
  const [olderDays, setOlderDays] = useState('90');
  const [loading, setLoading] = useState(true);
  const longPressRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      const [records, nextCounts] = await Promise.all([
        db.jobs.orderBy('createdAt').reverse().toArray(),
        getRecordCounts()
      ]);

      setJobs(records);
      setCounts(nextCounts);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => {
      setJobs([]);
      setLoading(false);
    });
  }, [refresh]);

  useEffect(() => {
    setSelected([]);
    setSelectionMode(false);
  }, [tab]);

  const visible = jobs
    .filter((job) => (tab === 'active' ? !job.archivedAt : job.archivedAt))
    .filter((job) => {
      const value = `${job.title} ${job.client} ${(job.tags || []).join(
        ' '
      )}`.toLowerCase();

      return value.includes(query.toLowerCase());
    });

  const selectedVisible = visible.filter((job) => selected.includes(job.id));
  const allVisibleSelected =
    visible.length > 0 &&
    visible.every((job) => selected.includes(job.id));

  const toggleSelection = (id) => {
    setSelectionMode(true);
    setSelected((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  const selectAll = () => {
    setSelectionMode(true);
    setSelected(allVisibleSelected ? [] : visible.map((job) => job.id));
  };

  const runArchiveAction = async () => {
    if (!selected.length) return;

    try {
      if (tab === 'active') {
        await archiveRecords(selected);
      } else {
        await restoreRecords(selected);
      }

      await refresh();
      setSelected([]);
      setSelectionMode(false);
    } catch {
      window.alert('The records could not be updated. Try again.');
    }
  };

  const permanentlyDelete = async () => {
    if (!selected.length) return;

    const confirmed = window.confirm(
      `Delete ${selected.length} record${
        selected.length === 1 ? '' : 's'
      } permanently? This also deletes the stored photos and PDF.`
    );

    if (!confirmed) return;

    try {
      await deleteRecordsPermanently(selected);
      await refresh();
      setSelected([]);
      setSelectionMode(false);
    } catch {
      window.alert('The records could not be deleted. Try again.');
    }
  };

  const preselectOlder = async () => {
    const ids = await getOlderRecordIds(Number(olderDays));
    const visibleIds = new Set(visible.map((job) => job.id));
    const matching = ids.filter((id) => visibleIds.has(id));

    setSelectionMode(true);
    setSelected(matching);
  };

  const startLongPress = (id) => {
    window.clearTimeout(longPressRef.current);
    longPressRef.current = window.setTimeout(() => {
      toggleSelection(id);
    }, 550);
  };

  const cancelLongPress = () => {
    window.clearTimeout(longPressRef.current);
  };

  return (
    <main className="screen">
      <header className="topbar">
        <div>
          <small>Local records</small>
          <h1>Proofs</h1>
        </div>
        <button
          type="button"
          className="button secondary"
          onClick={() => setSelectionMode((value) => !value)}
        >
          {selectionMode ? 'Done' : 'Select'}
        </button>
      </header>

      <div
        role="tablist"
        aria-label="Record status"
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'active'}
          className={`button ${tab === 'active' ? 'primary' : 'secondary'}`}
          onClick={() => setTab('active')}
        >
          Active ({counts.active})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'archive'}
          className={`button ${tab === 'archive' ? 'primary' : 'secondary'}`}
          onClick={() => setTab('archive')}
        >
          Archive ({counts.archived})
        </button>
      </div>

      <input
        className="search"
        type="search"
        placeholder={
          tab === 'active'
            ? 'Search active proofs'
            : 'Search archived proofs'
        }
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {tab === 'active' && (
        <section className="form-card">
          <label>
            Archive older than
            <select
              value={olderDays}
              onChange={(event) => setOlderDays(event.target.value)}
              style={{
                display: 'block',
                width: '100%',
                minHeight: 48,
                marginTop: 8,
                padding: '0 10px',
                border: '2px solid var(--line)',
                borderRadius: 10,
                background: 'var(--panel)',
                color: 'var(--ink)'
              }}
            >
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="180">180 days</option>
              <option value="365">1 year</option>
            </select>
          </label>
          <button
            type="button"
            className="button secondary"
            onClick={preselectOlder}
          >
            Select matching records
          </button>
          <p style={{ marginTop: 10, color: 'var(--muted)' }}>
            This only selects records. Nothing is archived until you confirm
            the Archive action below.
          </p>
        </section>
      )}

      {selectionMode && (
        <div
          className="form-card"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10
          }}
        >
          <strong>{selected.length} selected</strong>
          <button
            type="button"
            className="button ghost"
            onClick={selectAll}
          >
            {allVisibleSelected ? 'Select none' : 'Select all'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="empty">
          <p>Loading records…</p>
        </div>
      ) : !visible.length ? (
        <div className="empty">
          <h2>
            {tab === 'active'
              ? 'No matching active proofs'
              : 'No archived proofs'}
          </h2>
          <p>
            {tab === 'active'
              ? 'Capture completed work to create your first local record.'
              : 'Archived proofs will appear here.'}
          </p>
        </div>
      ) : (
        <div className="record-grid">
          {visible.map((job) => (
            <RecordCard
              key={job.id}
              job={job}
              selectionMode={selectionMode}
              selected={selected.includes(job.id)}
              onToggle={() => toggleSelection(job.id)}
              onPointerDown={() => startLongPress(job.id)}
              onPointerUp={cancelLongPress}
              onPointerLeave={cancelLongPress}
            />
          ))}
        </div>
      )}

      {selectionMode && selectedVisible.length > 0 && (
        <div
          className="action-dock"
          style={{ bottom: 64, flexWrap: 'wrap' }}
        >
          <strong style={{ width: '100%' }}>
            {selectedVisible.length} selected
          </strong>
          {tab === 'active' ? (
            <button
              type="button"
              className="button primary"
              onClick={runArchiveAction}
            >
              Archive selected
            </button>
          ) : (
            <>
              <button
                type="button"
                className="button primary"
                onClick={runArchiveAction}
              >
                Restore selected
              </button>
              <button
                type="button"
                className="button danger"
                onClick={permanentlyDelete}
              >
                Delete permanently
              </button>
            </>
          )}
        </div>
      )}
    </main>
  );
}

function RecordCard({
  job,
  selectionMode,
  selected,
  onToggle,
  onPointerDown,
  onPointerUp,
  onPointerLeave
}) {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!job.thumbnail) return undefined;

    const next = URL.createObjectURL(job.thumbnail);
    setUrl(next);

    return () => URL.revokeObjectURL(next);
  }, [job.thumbnail]);

  const content = (
    <>
      {url ? (
        <img src={url} alt="" />
      ) : (
        <div className="record-placeholder">No photo</div>
      )}
      <div>
        <small>{new Date(job.createdAt).toLocaleDateString()}</small>
        <h2>{job.title}</h2>
        <p>{job.client}</p>
      </div>
    </>
  );

  if (selectionMode) {
    return (
      <div
        className="record-card"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        <div
          style={{
            position: 'absolute',
            zIndex: 1,
            margin: 8,
            padding: 4,
            borderRadius: 8,
            background: 'var(--panel)'
          }}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Select ${job.title}`}
          />
        </div>
        {content}
      </div>
    );
  }

  return (
    <NavLink to={`/records/${job.id}`} className="record-card">
      {content}
    </NavLink>
  );
}

function RecordDetail({ setNotice }) {
  const { id } = useParams();
  const record = useRecord(id);
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(null);

  const copyFingerprint = async () => {
    try {
      await navigator.clipboard.writeText(record.sha256);
      setNotice('Fingerprint copied.');
    } catch {
      setNotice(record.sha256);
    }
  };

  const verifyIntegrity = async () => {
    if (!record?.pdf || !record.sha256) return;

    setVerifying('busy');

    try {
      const digest = await hashPdf(record.pdf);

      setVerifying(digest === record.sha256);
    } catch {
      setVerifying(false);
    }
  };

  if (record === null) {
    return <main className="screen centered">Loading proof…</main>;
  }

  if (!record) {
    return (
      <main className="screen centered">
        This proof was not found.
      </main>
    );
  }

  const name = safeFileName(record.title, id);

  return (
    <main className="screen detail">
      <header className="topbar">
        <div>
          <small>Approved proof</small>
          <h1>{record.title}</h1>
        </div>
      </header>

      <section className="form-card">
        <p>
          <strong>{record.client}</strong>
        </p>
        {record.signerName && <p>Signed by: {record.signerName}</p>}
        {record.signerType && <p>Signing as: {record.signerType}</p>}
        {record.jobRef && <p>Job ref: {record.jobRef}</p>}
        {record.address && <p>Site: {record.address}</p>}
        <p>{new Date(record.createdAt).toLocaleString()}</p>
        <p>{record.tags?.join(' · ') || 'No tags'}</p>
        {record.sha256 && (
          <p className="fingerprint" title={record.sha256}>
            SHA-256 fingerprint: {record.sha256.slice(0, 32)}…
            <br />
            <button
              type="button"
              className="button ghost"
              style={{ marginTop: 8 }}
              onClick={copyFingerprint}
            >
              Copy fingerprint
            </button>
          </p>
        )}
      </section>

      {verifying !== null && (
        <section className="form-card">
          {verifying === 'busy' ? (
            <p>Checking this PDF against its recorded fingerprint…</p>
          ) : verifying ? (
            <p className="verify-pass">
              Verified. This PDF matches its SHA-256 fingerprint recorded at
              approval.
            </p>
          ) : (
            <p className="warning">
              Mismatch. This PDF no longer matches the fingerprint recorded at
              approval — it may have changed since it was signed off.
            </p>
          )}
        </section>
      )}

      <div className="stack">
        <button
          className="button primary"
          onClick={() =>
            sharePdf(record.pdf, name, record.title).catch((error) =>
              setNotice(error.message)
            )
          }
        >
          Share PDF
        </button>
        <button
          className="button secondary"
          onClick={() => openBlob(record.pdf)}
        >
          Open PDF
        </button>
        <button
          className="button secondary"
          onClick={() => downloadBlob(record.pdf, name)}
        >
          Download
        </button>
        {record.sha256 && (
          <button className="button secondary" onClick={verifyIntegrity}>
            Verify integrity
          </button>
        )}
        <button
          className="button danger"
          onClick={async () => {
            if (
              !window.confirm(
                'Delete this proof and all of its photos from this device?'
              )
            ) {
              return;
            }

            try {
              await deleteJob(Number(id));
              navigate('/records', { replace: true });
            } catch {
              setNotice('The proof could not be deleted. Try again.');
            }
          }}
        >
          Delete proof
        </button>
      </div>
    </main>
  );
}

function WatermarkPreview({ watermark, logoUrl }) {
  // Live, low-cost preview: pure CSS transforms over a placeholder photo —
  // no re-render of real photos, no PDF build. Mirrors drawPhotoWatermark:
  // the watermark is drawn per photo, so the preview frame is a photo rect,
  // not a page.
  if (!watermark || watermark.mode === 'off') {
    return (
      <div className="wm-preview">
        <div className="wm-preview-page">
          <span className="hint">Watermark off</span>
        </div>
      </div>
    );
  }

  if (watermark.mode === 'logo' && !logoUrl) {
    return (
      <div className="wm-preview">
        <div className="wm-preview-page">
          <span className="hint">Upload a logo in Business profile first</span>
        </div>
      </div>
    );
  }

  const opacity = Math.min(
    1,
    Math.max(0.05, Number(watermark.opacity) || 0.35)
  );
  const placement = watermark.placement || 'centre-diagonal';

  // drawPhotoWatermark size logic: the setting is a cap, scaled down to what
  // the photo rect can hold (min side / 8 heuristic, 8pt floor). The preview
  // card is ~300px wide like a 260pt photo, so reuse the same ratio.
  const cardWidth = 300;
  const minSide = Math.min(cardWidth, 200);
  const size = Math.min(Number(watermark.size) || 36, minSide / 8);
  const fontScale = size / 36;

  const content =
    watermark.mode === 'text' ? (
      <span
        className="wm-preview-text"
        style={{ color: watermark.colour, opacity, fontSize: `${fontScale}em` }}
      >
        {watermark.text}
      </span>
    ) : (
      <img
        className="wm-preview-logo"
        style={{
          color: watermark.colour,
          opacity,
          width: `${fontScale * 4}em`,
          height: `${fontScale * 4}em`,
          objectFit: 'contain'
        }}
        src={logoUrl}
        alt=""
      />
    );

  return (
    <div className="wm-preview" aria-label="Watermark preview">
      <div className="wm-preview-page">
        {placement === 'tiled' ? (
          <div className="wm-tiled">{content}</div>
        ) : placement === 'bottom-right' ? (
          <div className="wm-bottom-right">{content}</div>
        ) : (
          <div className="wm-diagonal">{content}</div>
        )}
      </div>
    </div>
  );
}

function Settings({ installEvent, setNotice }) {
  const [storage, setStorage] = useState(null);
  const fileRef = useRef(null);
  const logoFileRef = useRef(null);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [logoUrl, setLogoUrl] = useState('');
  const [signerType, setSignerType] = useState('');

  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings()
      .then((value) => {
        setSettings(value);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  // Object URL lifecycle for the stored logo blob (raster or SVG).
  useEffect(() => {
    const blob = settings?.logo?.blob;

    if (!blob) {
      setLogoUrl('');
      return undefined;
    }

    let cancelled = false;
    let objectUrl = '';

    if (blob.type === 'image/svg+xml') {
      blob
        .text()
        .then((text) => {
          if (cancelled) return;

          try {
            setLogoUrl(svgDataUrl(text));
          } catch {
            objectUrl = URL.createObjectURL(blob);
            setLogoUrl(objectUrl);
          }
        })
        .catch(() => {});
    } else {
      objectUrl = URL.createObjectURL(blob);
      setLogoUrl(objectUrl);
    }

    return () => {
      cancelled = true;

      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [settings?.logo?.blob]);

  const refresh = () =>
    storageEstimate()
      .then(setStorage)
      .catch(() => setStorage(null));

  useEffect(() => {
    refresh();
  }, []);

  const update = async (mutate) => {
    const next = mutate(settings);

    setSettings(next);
    setSaving(true);

    try {
      const saved = await saveSettings(next);

      setSettings(saved);
    } catch (error) {
      setNotice(error.message || 'The setting could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const onLogoFile = async (event) => {
    const file = event.target.files?.[0];

    event.target.value = '';

    if (!file) return;

    try {
      const normalized = await normalizeLogo(file);

      await saveLogo(normalized);
      setSettings((current) => ({ ...current, logo: normalized }));
      setNotice('Logo saved.');
    } catch (error) {
      setNotice(error.message);
    }
  };

  const removeLogo = async () => {
    try {
      await clearLogo();
      setSettings((current) => ({ ...current, logo: null }));
      setNotice('Logo removed.');
    } catch (error) {
      setNotice(error.message);
    }
  };

  const addSignerType = () => {
    const type = signerType.trim();

    if (!type) return;
    if (settings.signerTypes?.some((value) => value.toLowerCase() === type.toLowerCase())) {
      setSignerType('');
      return;
    }

    setSignerType('');

    update((current) => ({
      ...current,
      signerTypes: [...(current.signerTypes || []), type]
    }));
  };

  return (
    <main className="screen">
      <header className="topbar">
        <div>
          <small>Device controls</small>
          <h1>Settings</h1>
        </div>
      </header>

      {!loaded ? (
        <div className="empty">
          <p>Loading settings…</p>
        </div>
      ) : (
        <>
          <section className="form-card">
            <h2>Business profile</h2>
            <p className="hint">
              Shown in the header of every PDF proof alongside the logo.
            </p>
            {['name', 'phone', 'email', 'address'].map((field) => (
              <label key={field}>
                {field === 'name' ? 'Business name' : field[0].toUpperCase() + field.slice(1)}
                <input
                  value={settings.business[field] || ''}
                  autoComplete="off"
                  onChange={(event) =>
                    update((current) => ({
                      ...current,
                      business: {
                        ...current.business,
                        [field]: event.target.value
                      }
                    }))
                  }
                />
              </label>
            ))}
          </section>

          <section className="form-card">
            <h2>Logo</h2>
            <p className="hint">
              PNG, JPEG, or SVG. Raster logos max {LOGO_MAX_DIM} × {LOGO_MAX_DIM} px;
              input limit 2 MB. SVG is embedded in the PDF as a rendered PNG.
            </p>

            <div className="logo-row">
              {logoUrl ? (
                <img className="logo-preview" src={logoUrl} alt="Business logo" />
              ) : (
                <div className="logo-preview logo-empty">No logo</div>
              )}

              <div className="stack" style={{ gap: 8 }}>
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => logoFileRef.current.click()}
                >
                  {logoUrl ? 'Replace logo' : 'Upload logo'}
                </button>
                {logoUrl && (
                  <button
                    type="button"
                    className="button ghost"
                    onClick={removeLogo}
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>

            <input
              ref={logoFileRef}
              hidden
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              onChange={onLogoFile}
            />
          </section>

          <section className="form-card">
            <h2>Signer types</h2>
            <p className="hint">
              Roles offered on the approval screen when someone signs a proof,
              e.g. Client or Site Manager.
            </p>

            {(settings.signerTypes || []).map((type, index) => (
              <div
                className="geotag-row"
                key={`${type}-${index}`}
              >
                <span className="hint">{type}</span>
                <button
                  type="button"
                  className="button ghost"
                  onClick={() =>
                    update((current) => ({
                      ...current,
                      signerTypes: current.signerTypes.filter(
                        (_, typeIndex) => typeIndex !== index
                      )
                    }))
                  }
                >
                  Remove
                </button>
              </div>
            ))}

            {!settings.signerTypes?.length && (
              <p className="hint">No signer types yet.</p>
            )}

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginTop: 10
              }}
            >
              <input
                value={signerType}
                autoComplete="off"
                placeholder="e.g. Site Manager"
                style={{ flex: 1, minWidth: 0, marginTop: 0 }}
                onChange={(event) => setSignerType(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addSignerType();
                  }
                }}
              />
              <button
                type="button"
                className="button secondary"
                onClick={addSignerType}
              >
                Add
              </button>
            </div>
          </section>

          <section className="form-card">
            <h2>Watermark</h2>
            <p className="hint">
              Applied over each photo when the PDF is built. Stored photos,
              the signature, and the approval statement are never modified.
            </p>

            <div
              role="tablist"
              aria-label="Watermark mode"
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}
            >
              {['off', 'text', 'logo'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={settings.watermark.mode === mode}
                  className={`button ${settings.watermark.mode === mode ? 'primary' : 'secondary'}`}
                  onClick={() =>
                    update((current) => ({
                      ...current,
                      watermark: { ...current.watermark, mode }
                    }))
                  }
                >
                  {mode === 'off'
                    ? 'Off'
                    : mode === 'text'
                      ? 'Text'
                      : 'Logo'}
                </button>
              ))}
            </div>

            {settings.watermark.mode !== 'off' && (
              <>
                {settings.watermark.mode === 'text' && (
                  <label>
                    Watermark text
                    <input
                      value={settings.watermark.text}
                      onChange={(event) =>
                        update((current) => ({
                          ...current,
                          watermark: {
                            ...current.watermark,
                            text: event.target.value
                          }
                        }))
                      }
                    />
                  </label>
                )}

                <label>
                  Size ({settings.watermark.size} pt)
                  <input
                    type="range"
                    min="8"
                    max="96"
                    step="2"
                    value={settings.watermark.size}
                    onChange={(event) =>
                      update((current) => ({
                        ...current,
                        watermark: {
                          ...current.watermark,
                          size: Number(event.target.value)
                        }
                      }))
                    }
                  />
                </label>

                {settings.watermark.mode === 'text' && (
                  <label>
                    Colour
                    <input
                      type="color"
                      value={settings.watermark.colour}
                      onChange={(event) =>
                        update((current) => ({
                          ...current,
                          watermark: {
                            ...current.watermark,
                            colour: event.target.value
                          }
                        }))
                      }
                    />
                  </label>
                )}

                <label>
                  Opacity ({Math.round(settings.watermark.opacity * 100)}%)
                  <input
                    type="range"
                    min="0.05"
                    max="1"
                    step="0.05"
                    value={settings.watermark.opacity}
                    onChange={(event) =>
                      update((current) => ({
                        ...current,
                        watermark: {
                          ...current.watermark,
                          opacity: Number(event.target.value)
                        }
                      }))
                    }
                  />
                </label>

                <label>
                  Placement
                  <select
                    value={settings.watermark.placement}
                    onChange={(event) =>
                      update((current) => ({
                        ...current,
                        watermark: {
                          ...current.watermark,
                          placement: event.target.value
                        }
                      }))
                    }
                    style={{
                      display: 'block',
                      width: '100%',
                      minHeight: 48,
                      marginTop: 8,
                      padding: '0 10px',
                      border: '2px solid var(--line)',
                      borderRadius: 10,
                      background: 'var(--panel)',
                      color: 'var(--ink)'
                    }}
                  >
                    <option value="centre-diagonal">Centre diagonal</option>
                    <option value="bottom-right">Bottom-right</option>
                    <option value="tiled">Tiled</option>
                  </select>
                </label>

                <WatermarkPreview
                  watermark={settings.watermark}
                  logoUrl={settings.logo ? logoUrl : ''}
                />
              </>
            )}
            {saving && <p className="hint">Saving…</p>}
          </section>

          <section className="form-card">
            <h2>Device storage</h2>
            <p>
              {storage
                ? `${formatBytes(storage.usage)} used of ${formatBytes(
                    storage.quota
                  )} (${storage.percent}%)`
                : 'Storage estimate unavailable.'}
            </p>
            <progress max="100" value={storage?.percent || 0} />
            <p className="warning">
              Records exist only on this device. Export backups regularly.
            </p>
          </section>

          <section className="form-card stack">
            <h2>Backup and restore</h2>
            <button
              className="button primary"
              onClick={async () => {
                try {
                  downloadBlob(
                    await exportBackup(),
                    `sign-off-backup-${new Date()
                      .toISOString()
                      .slice(0, 10)}.zip`
                  );
                } catch (error) {
                  setNotice(error.message);
                }
              }}
            >
              Export backup
            </button>
            <button
              className="button secondary"
              onClick={() => fileRef.current.click()}
            >
              Import backup
            </button>
            <input
              ref={fileRef}
              hidden
              type="file"
              accept=".zip,application/zip"
              onChange={async (event) => {
                try {
                  const file = event.target.files?.[0];
                  if (!file) return;

                  await importBackup(file);
                  refresh();
                  const reloaded = await getSettings();

                  setSettings(reloaded);
                  setNotice(
                    'Backup imported. Existing records were not duplicated.'
                  );
                } catch (error) {
                  setNotice(error.message);
                } finally {
                  event.target.value = '';
                }
              }}
            />
          </section>

          <section className="form-card">
            <h2>Install</h2>
            <button
              className="button secondary"
              disabled={!installEvent}
              onClick={async () => {
                await installEvent.prompt();
                await installEvent.userChoice;
              }}
            >
              {installEvent
                ? 'Install Sign-off'
                : 'Install from your browser menu'}
            </button>
          </section>
        </>
      )}
    </main>
  );
}

function BottomNav() {
  const location = useLocation();

  if (
    location.pathname.startsWith('/review') ||
    location.pathname.startsWith('/success')
  ) {
    return null;
  }

  return (
    <nav className="bottom-nav" aria-label="Main navigation">
      <NavLink to="/capture">Capture</NavLink>
      <NavLink to="/records">Records</NavLink>
      <NavLink to="/settings">Settings</NavLink>
    </nav>
  );
}

export default function App() {
  return (
    <HashRouter>
      <AppShell />
    </HashRouter>
  );
}